import { useLayoutEffect, useState, type RefCallback } from "react";

/**
 * Grow a textarea to fit its content so all text is visible without an
 * inner scrollbar. Returns a callback ref to spread onto the textarea.
 *
 * Using a callback ref (element kept in state) is deliberate: the
 * measure re-runs the moment the element ATTACHES to the DOM — e.g.
 * when a collapsed note / journal entry expands and the textarea
 * mounts. A plain `useRef` + effect keyed on `[ref, value]` misses
 * that (the ref object is stable and `value` doesn't change on
 * expand), so the first expand wouldn't resize.
 *
 * Pair with CSS `overflow: hidden` + `resize: none`.
 */
export function useAutosizeTextarea(
  value: string,
): RefCallback<HTMLTextAreaElement> {
  const [el, setEl] = useState<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    if (!el) return;
    // Reset first so shrinking works, then size to the content.
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [el, value]);
  return setEl;
}
